<template>
  <draggable
    class="gis-sortable-tree"
    :class="uniqueClass"
    tag="ul"
    :list="data"
    :group="{ name: group }"
    :item-key="itemKey"
    :move="onMove"
    :disabled="!isSortable"
    @end="onEnd"
    @add="onAdd($event, data)"
  >
    <template #item="{ element }">
      <li>
        <div
          class="gis-tree-item"
          :class="{ selected: isSelectedElement(element), disabled: isDisabled(element)}"
          v-tooltip.left="element.disabledMessage || ''"
          @click.prevent.stop="selectElement(element)"
          @contextmenu.prevent.stop="e => $emit('openMenu', e, element)"
          tabindex="0"
        >
          <div class="flex">
            <div class="gis-tree-icon">
              <template v-if="!element.icon">
                <i
                  v-if="element.hasOwnProperty(childrenProperty) && element[childrenProperty]"
                  class="fa mr-1"
                  :class="{
                    'fa-folder-open': !asTreeInput && !isClosedElement(element),
                    'fa-folder': !asTreeInput && isClosedElement(element),
                    'fa-minus-square-o': asTreeInput && !isClosedElement(element),
                    'fa-plus-square-o': asTreeInput && isClosedElement(element),
                  }"
                  @click.stop="toggleElement(element)"
                />
                <i v-else-if="!asTreeInput" class="fa fa-file-o mr-1"/>
              </template>
              <i v-else class="mr-1" :class="element.icon" @click.stop="toggleElement(element)"/>
            </div>
            <span v-if="isTemplateVariable(labelProperty)" v-html="labelProperty.interpolateObject(element)"></span>
            <span v-else-if="element.hasOwnProperty(labelProperty)" v-html="stripTags ? stripHtml(element[labelProperty]) : element[labelProperty]"></span>
            <span v-else v-html="element"></span>
          </div>
          <div v-if="isSelectedElement(element) && (allowActions.length || extraActions || parentActions)">
            <template v-if="element.hasOwnProperty(childrenProperty) && element[childrenProperty] && parentActions && parentActions.length">
              <i
                v-for="(act) in parentActions"
                class="ml-2"
                v-tooltip="act.title"
                :class="act.icon"
                @click.prevent.stop="onActionClick(act.code, element)"
              />
            </template>

            <template v-if="extraActions && extraActions.length">
              <i
                v-for="(act) in extraActions"
                class="ml-2"
                v-tooltip="act.title"
                :class="act.icon"
                @click.prevent.stop="onActionClick(act.code, element)"
              />
            </template>

            <template v-if="allowActions && allowActions.length">
              <i
                v-if="allowActions.indexOf('schema') > -1"
                class="fa fa-table ml-2"
                v-tooltip="`Edit Schema`"
                @click.prevent.stop="onActionClick('schema', element)"
              />
              <i
                v-if="allowActions.indexOf('edit') > -1"
                class="fa fa-pencil ml-2"
                v-tooltip="`Edit`"
                @click.prevent.stop="onActionClick('edit', element)"
              />
              <i
                v-if="allowActions.indexOf('add') > -1"
                class="fa fa-plus ml-2"
                v-tooltip="`Add`"
                @click.prevent.stop="onActionClick('add', element)"
              />
              <i
                v-if="allowActions.indexOf('delete') > -1"
                class="fa fa-trash-o ml-2"
                v-tooltip="`Delete`"
                @click.prevent.stop="onActionClick('delete', element)"
              />
            </template>
          </div>
        </div>
        <SortableTree
          v-if="element.hasOwnProperty(childrenProperty) && element[childrenProperty] && !isClosedElement(element)"
          v-model:data="element[childrenProperty]"
          :item-key="itemKey"
          :group="element.code"
          :item-parent-key="itemParentKey"
          :label-property="labelProperty"
          :children-property="childrenProperty"
          :selected="selectedElement"
          :parent="parent"
          :is-sortable="isSortable"
          :allowActions="allowActions"
          :extra-actions="extraActions"
          :parent-actions="parentActions"
          :as-tree-input="asTreeInput"
          :is-child="true"
          :strip-tags="stripTags"
          @select="selectChildElement"
          @moved="onMoved"
          @action="onActionClick"
          @openMenu="(eChild,elementChild) => $emit('openMenu', eChild, elementChild)"
        />
      </li>
    </template>
  </draggable>
</template>

<script>
import draggable from "vuedraggable";
import {stripHtml} from "@/js/utils";
import {isTemplateVariable} from "@/js/document/document";

export default {
  name: "SortableTree",
  components: {
    draggable,
  },
  props: {
    data: {
      required: true,
      type: Array,
    },
    itemKey: {
      type: String,
      default: "moduleId",
    },
    group: {
      type: String,
      default: 'gis-tree'
    },
    itemParentKey: {
      type: String,
      default: "parentModuleId",
    },
    labelProperty: {
      type: String,
      default: "title",
    },
    childrenProperty: {
      type: String,
      default: "children",
    },
    selected: {
      type: Object,
    },
    parent: {
      type: Object,
      default: () => {
        return {
          newParentId: null,
        };
      },
    },
    isSortable: {
      type: Boolean,
      default: true,
    },
    allowActions: {
      type: Array,
      default: function () {
        return ["add", "delete"];
      },
    },
    extraActions: {
      type: Array
    },
    parentActions: {
      type: Array
    },
    asTreeInput: {
      type: Boolean,
      default: false,
    },
    disableBranchNodes: {
      type: Boolean,
      default: false
    },
    isChild: {
      type: Boolean,
      default: false
    },
    stripTags: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      selectedElement: null,
      dragElement: null,
      closedItems: [],
      currentNavIndex: -1,
      uniqueClass: 'tree-' + new Date().getTime()
    };
  },
  mounted() {
    this.selectedElement = this.selected && typeof this.selected === 'object' ? JSON.parse(JSON.stringify(this.selected)) : this.selected;

    if (this.asTreeInput) {
      document.addEventListener("keydown", this.keyboardNav);
    }
  },
  beforeUnmount() {
    document.removeEventListener("keydown", this.keyboardNav);
  },
  updated() {
    this.selectedElement = this.selected && typeof this.selected === 'object' ? JSON.parse(JSON.stringify(this.selected)) : this.selected;
  },
  methods: {
    keyboardNav(event) {
      if (event.key === "ArrowDown") {
        this.currentNavIndex = (this.currentNavIndex + 1) % this.data.length; // Move down
      } else if (event.key === "ArrowUp") {
        this.currentNavIndex = (this.currentNavIndex - 1 + this.data.length) % this.data.length; // Move up
      } else if (event.key === "Enter") {
        this.selectElement(this.data[this.currentNavIndex]);
      } else {
        return;
      }

      this.updateHighlight();
      event.preventDefault(); // Prevent default scrolling behavior
    },
    updateHighlight() {
      const listItems = document.querySelectorAll(`.${this.uniqueClass} .gis-tree-item`);
      listItems.forEach((item, index) => {
        if (index === this.currentNavIndex) {
          item.focus();
        }
      });
    },
    isDisabled(opt) {
      return (this.disableBranchNodes && !this.isChild && opt?.hasOwnProperty(this.childrenProperty)) || opt?.disabled === true
    },
    isTemplateVariable(val) {
      return isTemplateVariable(val)
    },
    onAdd(evt, group) {
      this.parent.newParentId = group.find(
        (el) => el[this.itemKey] !== this.dragElement?.data[this.itemKey]
      )[this.itemParentKey];
    },
    onEnd() {
      this.$emit("moved", {
        ...this.dragElement,
        newParentId: this.parent.newParentId,
      });
    },
    onMoved(element) {
      this.$emit("moved", element);
    },
    onMove(evt) {
      this.dragElement = {
        data: evt.draggedContext.element,
        oldIndex: evt.draggedContext.index,
        newIndex: evt.draggedContext.futureIndex,
      };
      this.parent.newParentId = this.dragElement?.data[this.itemParentKey];
    },
    selectElement(element) {
      if (this.isDisabled(element)) return

      if (Array.isArray(this.selectedElement)) {
        this.selectedElement.push(element)
      } else {
        this.selectedElement = element;
      }
      this.$emit("select", element, this.isChild);
    },
    selectChildElement(element) {
      if (Array.isArray(this.selectedElement)) {
        this.selectedElement.push(element)
      } else {
        this.selectedElement = element;
      }
      this.$emit("select", element, true);
    },
    isClosedElement(element) {
      return this.closedItems.indexOf(element[this.itemKey]) > -1;
    },
    isSelectedElement(element) {
      if (!this.selectedElement) return false

      if (Array.isArray(this.selectedElement)) {
        let exists = this.selectedElement.find((el) =>
          el === element ||
          (this.itemKey &&
            (
              el === element[this.itemKey] ||
              el[this.itemKey] === element ||
              el[this.itemKey] === element[this.itemKey]
            )
          )
        )
        return !!exists
      } else if (typeof this.selectedElement === 'object' && this.itemKey) {
        return this.selectedElement[this.itemKey] === element[this.itemKey] ||
          this.selectedElement === element[this.itemKey]
      } else {
        return this.selectedElement === element || (this.itemKey && this.selectedElement === element[this.itemKey])
      }
    },
    toggleElement(element) {
      if (this.isClosedElement(element)) {
        this.closedItems = this.closedItems.filter(
          (el) => el !== element[this.itemKey]
        );
      } else {
        this.closedItems.push(element[this.itemKey]);
      }
    },
    onActionClick(action, element) {
      this.$emit("action", action, element);
    },
    stripHtml(html) {
      return stripHtml(html)
    },
  },
};
</script>

<style scoped lang="scss">
.gis-sortable-tree {
  list-style: none;
  margin: 0;
  padding: 0;

  .gis-sortable-tree {
    margin-left: 20px;
  }
}

.gis-tree-item {
  padding: 5px;
  cursor: pointer;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1px;

  .gis-tree-icon {
    width: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 3px;
  }

  &:hover {
    background: #eee;
  }

  &.selected {
    color: white;
    background: #047EEF;
  }

  &.disabled {
    color: #aaa;
    cursor: not-allowed;
  }
}

.sortable-ghost {
  background: #8a8ae7;
  border-radius: 5px;
  color: #fff;
}

html.dark {
  .gis-tree-item:hover {
    background: #313249;
  }
}
</style>
